import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Copy, Eye, Pencil, Search, Trash2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import { FRAMEWORK_STUDIO_LABELS } from "../../utils/frameworkStudioLabels";
import ContentLoader from "../../components/ContentLoader";
import CatalogFilterChip from "../../components/sa/CatalogFilterChip";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button, Input, Modal, Pagination } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import { dashboardToast } from "../../utils/dashboardToast";
import {
  copyAssessmentBlueprint,
  deleteAssessmentBlueprint,
  getAssessmentBlueprints,
} from "../../services/assessmentBlueprintApi";
import { getCompanyList } from "../../services/companyApi";
import { getProjectList } from "../../services/projectApi";
import type { AssessmentBlueprintSummary } from "../../types/assessmentBlueprint";
import { AssessmentBlueprintWizardPanel } from "./AssessmentBlueprintWizardPanel";
import { AssessmentBlueprintViewModal } from "./AssessmentBlueprintViewModal";
import "../../assets/css/dashboard.css";

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

type WizardTarget = { mode: "create" } | { mode: "edit"; id: number };

type CopyFormState = {
  title: string;
  companyId: string;
  projectId: string;
};

const emptyCopyForm = (): CopyFormState => ({
  title: "",
  companyId: "",
  projectId: "",
});

const EmptyCell = () => <span className="app-td__empty">—</span>;

const blueprintStatusClass = (status: string) => {
  if (status === "Published") return "app-status app-status--success";
  if (status === "Draft") return "app-status app-status--warning";
  return "app-status app-status--muted";
};

const AssessmentBlueprints = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState<AssessmentBlueprintSummary[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, setSortKey] = useState<string>(TABLE_SORT.DATE_DESC);
  const [pageInfo, setPageInfo] = useState({ pageNumber: 1, pageSize: 10, totalCount: 0 });
  const [deleteTarget, setDeleteTarget] = useState<AssessmentBlueprintSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [copyTarget, setCopyTarget] = useState<AssessmentBlueprintSummary | null>(null);
  const [copyForm, setCopyForm] = useState<CopyFormState>(emptyCopyForm);
  const [copyError, setCopyError] = useState("");
  const [copying, setCopying] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [projectOptions, setProjectOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const [wizardTarget, setWizardTarget] = useState<WizardTarget | null>(null);
  const [wizardKey, setWizardKey] = useState(0);
  const [wizardSubtitle, setWizardSubtitle] = useState("New draft");

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getAssessmentBlueprints(
        pageInfo.pageNumber,
        pageInfo.pageSize,
        sortKey,
        searchTerm.trim(),
        statusFilter
      );
      setItems(result.items || []);
      setPageInfo((prev) => ({
        ...prev,
        totalCount: result.totalCount,
        pageNumber: result.pageNumber,
        pageSize: result.pageSize,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load assessments.";
      setError(message);
      dashboardToast.error(message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pageInfo.pageNumber, pageInfo.pageSize, searchTerm, sortKey, statusFilter]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!copyTarget) return;
    getCompanyList(1, 500, "name-asc", "")
      .then((result) =>
        setCompanyOptions(
          (result.items || []).map((c) => ({
            id: c.id ?? c.companyId,
            name: c.companyName ?? c.name ?? `Company ${c.id}`,
          }))
        )
      )
      .catch(() => setCompanyOptions([]));
  }, [copyTarget]);

  useEffect(() => {
    if (!copyTarget || !copyForm.companyId) {
      setProjectOptions([]);
      return;
    }

    setProjectsLoading(true);
    getProjectList(Number(copyForm.companyId))
      .then((projects) =>
        setProjectOptions(
          (projects || []).map((p) => ({
            id: p.id ?? p.projectId,
            name: p.projectName ?? p.name ?? `Project ${p.id}`,
          }))
        )
      )
      .catch(() => setProjectOptions([]))
      .finally(() => setProjectsLoading(false));
  }, [copyTarget, copyForm.companyId]);

  const openCreateWizard = useCallback(() => {
    setWizardTarget({ mode: "create" });
    setWizardKey((prev) => prev + 1);
  }, []);

  const openEditWizard = useCallback((id: number) => {
    setWizardTarget({ mode: "edit", id });
    setWizardKey((prev) => prev + 1);
  }, []);

  const [viewId, setViewId] = useState<number | null>(null);

  const openItem = useCallback(
    (item: AssessmentBlueprintSummary) => setViewId(item.id),
    []
  );

  // Set when the wizard created/saved a draft, so merely closing the modal still refreshes the list.
  const wizardChangedRef = useRef(false);
  const markWizardChanged = useCallback(() => {
    wizardChangedRef.current = true;
  }, []);

  const closeWizard = useCallback(() => {
    setWizardTarget(null);
    if (wizardChangedRef.current) {
      wizardChangedRef.current = false;
      loadItems();
    }
  }, [loadItems]);

  const completeWizard = useCallback(() => {
    wizardChangedRef.current = false;
    setWizardTarget(null);
    loadItems();
  }, [loadItems]);

  const openCopyModal = useCallback((item: AssessmentBlueprintSummary) => {
    setCopyTarget(item);
    setCopyForm({
      title: `Copy of ${item.title}`,
      companyId: "",
      projectId: "",
    });
    setCopyError("");
    setProjectOptions([]);
  }, []);

  const closeCopyModal = useCallback(() => {
    if (copying) return;
    setCopyTarget(null);
    setCopyForm(emptyCopyForm());
    setCopyError("");
  }, [copying]);

  // Deep links from /AssessmentBlueprints/new or /:id land here via location.state
  useEffect(() => {
    const raw = (location.state as { openBlueprintWizard?: number | "new" } | null)
      ?.openBlueprintWizard;
    if (raw == null) return;
    if (raw === "new") openCreateWizard();
    else if (typeof raw === "number" && raw > 0) openEditWizard(raw);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate, openCreateWizard, openEditWizard]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(pageInfo.totalCount / pageInfo.pageSize) || 1),
    [pageInfo.totalCount, pageInfo.pageSize]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleStatusFilterChange = (nextStatus: string) => {
    setStatusFilter(nextStatus);
    setPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleSortChange = (nextSort: string) => {
    setSortKey(nextSort);
    setPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const deleteTargetIsLiveAssignment =
    deleteTarget?.status === "Published" || Boolean(deleteTarget?.projectId);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAssessmentBlueprint(deleteTarget.id);
      dashboardToast.success("Assessment deleted.");
      setDeleteTarget(null);
      await loadItems();
    } catch (err) {
      dashboardToast.error(err instanceof Error ? err.message : "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCopy = async () => {
    if (!copyTarget) return;

    const title = copyForm.title.trim();
    const projectId = Number(copyForm.projectId);

    if (!title) {
      setCopyError("New assessment name is required.");
      return;
    }
    if (!copyForm.companyId) {
      setCopyError("Select a company.");
      return;
    }
    if (!projectId) {
      setCopyError("Select a project.");
      return;
    }

    setCopying(true);
    setCopyError("");
    try {
      const created = await copyAssessmentBlueprint({
        sourceBlueprintId: copyTarget.id,
        title,
        projectId,
      });
      dashboardToast.success("Assessment copied.");
      setCopyTarget(null);
      setCopyForm(emptyCopyForm());
      await loadItems();
      if (created?.id) {
        openEditWizard(created.id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Copy failed.";
      setCopyError(message);
      dashboardToast.error(message);
    } finally {
      setCopying(false);
    }
  };

  const wizardOpen = Boolean(wizardTarget);
  const wizardBlueprintId = wizardTarget?.mode === "edit" ? wizardTarget.id : null;
  const copyCanSubmit =
    Boolean(copyForm.title.trim()) && Boolean(copyForm.companyId) && Boolean(copyForm.projectId);

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle={FRAMEWORK_STUDIO_LABELS.assessmentAssignments}>
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">
                {FRAMEWORK_STUDIO_LABELS.assessmentAssignments}
              </h5>
              <p className="dash-list-toolbar__desc">
                {FRAMEWORK_STUDIO_LABELS.assessmentAssignmentsDescription}
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search title or framework…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <CatalogFilterChip
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                disabled={loading}
                options={[
                  { value: "Draft", label: "Draft" },
                  { value: "Published", label: "Published" },
                ]}
              />
              <TableSortSelect variant="chip" value={sortKey} onChange={handleSortChange} disabled={loading} />
              <Button variant="primary" className="sa-create-btn" onClick={openCreateWizard} disabled={loading}>
                {FRAMEWORK_STUDIO_LABELS.createAssessment}
              </Button>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading assessments…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>{FRAMEWORK_STUDIO_LABELS.assessmentFrameworkColumn}</th>
                      <th>Company</th>
                      <th>Project</th>
                      <th>Status</th>
                      <th className="app-th--num">Questions</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center">
                          <span className="app-td__desc">
                            No assessments yet. Use Create Assessment and choose an assessment
                            framework.
                          </span>
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => {
                        const description = item.description?.trim();
                        const questionCount = item.questionCount ?? 0;

                        return (
                          <tr
                            key={item.id}
                            className="rdm-row-clickable"
                            onClick={() => openItem(item)}
                          >
                            <td>
                              <span className="app-td__title">{item.title}</span>
                              {description ? (
                                <span className="app-td__desc" title={description}>
                                  {description}
                                </span>
                              ) : (
                                <span className="app-td__empty">No description</span>
                              )}
                            </td>
                            <td>
                              {item.sourceTraxccelFrameworkTitle ? (
                                <span className="app-td__desc">
                                  {item.sourceTraxccelFrameworkTitle}
                                </span>
                              ) : (
                                <EmptyCell />
                              )}
                            </td>
                            <td>
                              {item.companyName ? (
                                <span className="app-td__desc">{item.companyName}</span>
                              ) : (
                                <EmptyCell />
                              )}
                            </td>
                            <td>
                              {item.projectName ? (
                                <span className="app-td__desc">{item.projectName}</span>
                              ) : (
                                <EmptyCell />
                              )}
                            </td>
                            <td>
                              <span className={blueprintStatusClass(item.status)}>
                                {item.status}
                              </span>
                            </td>
                            <td className="app-td--num">
                              {questionCount > 0 ? (
                                <span className="app-td__num">{questionCount}</span>
                              ) : (
                                <EmptyCell />
                              )}
                            </td>
                            <td>
                              <span className="app-td__meta">
                                {formatDate(item.updatedAt || item.createdAt)}
                              </span>
                            </td>
                            <td
                              className="dash-mobile-table-actions"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <div className="btn-list-actions">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="btn-list-edit"
                                  title="View"
                                  aria-label={`View ${item.title}`}
                                  onClick={() => openItem(item)}
                                >
                                  <Eye />
                                </Button>
                                {item.status === "Draft" ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="btn-list-edit"
                                    title="Edit draft"
                                    aria-label={`Edit ${item.title}`}
                                    onClick={() => openEditWizard(item.id)}
                                  >
                                    <Pencil />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="btn-list-edit"
                                    style={{ visibility: "hidden" }}
                                    aria-hidden
                                    tabIndex={-1}
                                    disabled
                                  >
                                    <Pencil />
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="btn-list-edit"
                                  title="Copy assessment"
                                  aria-label={`Copy ${item.title}`}
                                  onClick={() => openCopyModal(item)}
                                  disabled={copying}
                                >
                                  <Copy />
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="btn-list-delete"
                                  title="Delete"
                                  aria-label={`Delete ${item.title}`}
                                  onClick={() => setDeleteTarget(item)}
                                  disabled={deleting}
                                >
                                  <Trash2 />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={pageInfo.pageNumber}
                totalPages={totalPages}
                onPageChange={(page) => setPageInfo((prev) => ({ ...prev, pageNumber: page }))}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      <Modal
        open={wizardOpen}
        onClose={closeWizard}
        size="3xl"
        allowMaximize
        className="ab-wizard-modal"
        subtitle={wizardOpen ? wizardSubtitle : undefined}
        title={
          wizardTarget?.mode === "create" && wizardBlueprintId == null
            ? FRAMEWORK_STUDIO_LABELS.createAssessment
            : "Assessment"
        }
      >
        {wizardOpen ? (
          <AssessmentBlueprintWizardPanel
            key={wizardKey}
            blueprintId={wizardBlueprintId}
            onSubtitleChange={setWizardSubtitle}
            onClose={closeWizard}
            onCompleted={completeWizard}
            onSaved={markWizardChanged}
          />
        ) : null}
      </Modal>

      <AssessmentBlueprintViewModal
        blueprintId={viewId}
        onClose={() => setViewId(null)}
        onEdit={(id) => {
          setViewId(null);
          openEditWizard(id);
        }}
        onDuplicate={(detail) => {
          setViewId(null);
          openCopyModal(detail);
        }}
      />

      <Modal
        open={Boolean(copyTarget)}
        onClose={closeCopyModal}
        title="Copy assessment"
        footer={
          <>
            <Button variant="outline" onClick={closeCopyModal} disabled={copying}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCopy}
              isLoading={copying}
              disabled={copying || !copyCanSubmit}
            >
              {copying ? "Copying…" : "Create copy"}
            </Button>
          </>
        }
      >
        <p className="app-modal-copy">
          Duplicates capabilities, sub-capabilities, and questions from{" "}
          <strong>{copyTarget?.title}</strong>. Only the name and project assignment are new; the
          copy starts as a draft.
        </p>

        {copyError ? (
          <Alert variant="error" className="mb-3">
            {copyError}
          </Alert>
        ) : null}

        <div className="mb-3">
          <Input
            id="ab-copy-title"
            label="New assessment name"
            required
            value={copyForm.title}
            onChange={(event) =>
              setCopyForm((prev) => ({ ...prev, title: event.target.value }))
            }
            disabled={copying}
            placeholder="Enter a name for the copy"
          />
        </div>

        <div className="app-field mb-3">
          <label htmlFor="ab-copy-company" className="app-field__label">
            Company
            <span className="app-field__required">*</span>
          </label>
          <select
            id="ab-copy-company"
            className="app-input"
            value={copyForm.companyId}
            disabled={copying}
            onChange={(event) =>
              setCopyForm((prev) => ({
                ...prev,
                companyId: event.target.value,
                projectId: "",
              }))
            }
          >
            <option value="">Select company…</option>
            {companyOptions.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="app-field">
          <label htmlFor="ab-copy-project" className="app-field__label">
            Project
            <span className="app-field__required">*</span>
          </label>
          <select
            id="ab-copy-project"
            className="app-input"
            value={copyForm.projectId}
            disabled={copying || !copyForm.companyId || projectsLoading}
            onChange={(event) =>
              setCopyForm((prev) => ({ ...prev, projectId: event.target.value }))
            }
          >
            <option value="">
              {!copyForm.companyId
                ? "Select a company first…"
                : projectsLoading
                  ? "Loading projects…"
                  : "Select project…"}
            </option>
            {projectOptions.map((project) => (
              <option key={project.id} value={String(project.id)}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title={deleteTargetIsLiveAssignment ? "Delete published assessment" : "Confirm delete"}
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleting} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete assessment"}
            </Button>
          </>
        }
      >
        {deleteTargetIsLiveAssignment ? (
          <div className="app-modal-copy">
            <Alert
              variant="warning"
              className="mb-3"
              title="This assessment is in use on a project"
              description="Deleting it removes the assessment snapshot and unlinks it from the project. Clients may lose the assessment structure and content they see until you publish and assign a different assessment."
            />
            <p className="mb-2">
              You are about to permanently delete <strong>{deleteTarget?.title}</strong>
              {deleteTarget?.projectName ? (
                <>
                  , currently assigned to project <strong>{deleteTarget.projectName}</strong>
                </>
              ) : null}
              .
            </p>
            <p className="mb-0">
              Assessment framework catalog data is not affected. You can create or publish
              another assessment and assign it to the same project afterward.
            </p>
          </div>
        ) : (
          <p className="m-0 app-modal-copy">
            Delete <strong>{deleteTarget?.title}</strong>? This cannot be undone. Catalog data is
            unaffected.
          </p>
        )}
      </Modal>
    </DashboardShell>
  );
};

export default AssessmentBlueprints;
